import * as React from "react";
import { connect } from "react-redux";
import _ from "lodash";
import { Dispatch, AnyAction } from "redux";
import { Container, Row, Col } from "react-bootstrap";
import { withRouter, RouteComponentProps } from "react-router-dom";
import {
    Table,
    Loading,
    IncrementalLoading,
    InlineError,
    CommonHeader,
    ProfileHead,
    ProfileHeadProps,
    CommonHeaderProps,
} from "../../components";
import {
    RequiredReportingProps,
    RequiredReportingDispatchProps,
    RequiredReportingState,
    TableRowProps,
    queryRequest,
    programmingInfo,
    TimeRange,
    hydrateCatalogPayload,
    overviewRequestPayload,
    timeRangePayload,
    track,
    CatalogItemType,
    createCsvFileRequestPayload,
    clientMetricsPayload,
    EntityType,
    BundleMap,
} from "../../../models";
import * as rootStyles from "../../styles";
import {
    RootState,
    playlistScreenSelector,
    reportingActions,
    catalogActions,
    userActions,
    csvActions,
    clientMetricsActions,
} from "../../../store";
import {
    getLocalizedString,
    generatePlaylistRows,
    paths,
    HYDRATION_COUNT,
    generateQueryWithoutGranularity,
    guessTimeZone,
    testIDSuffixes,
    getNewPlaylistAsins,
    hydrationHelpers,
    buildUIClickPayload,
    DeviceUtil,
} from "../../../utils";
import { stringIds, ImageList, bundleIds } from "../../../assets";
import {
    CsvButtonId,
    csvButtonLoadingStatusData,
    CsvType,
} from "../../../export";
import { buttonIds, pageIds } from "@amzn/ziggy-asset";

const testIDPrefix = "PlaylistsScreen";
const metricPrefix = "playlistsPage";
const pagePath = paths.playlists;

type ViewProps = {
    trackAsin?: string;
};

type StateProps = RequiredReportingProps & {
    playlists: programmingInfo[];
    reportingKey: string;
    selectedSongAsin?: string;
    csvButtonLoadingStatus: csvButtonLoadingStatusData;
    hasCsvExportCapabilities: boolean;
    bundleMap: BundleMap;
};

type DispatchProps = RequiredReportingDispatchProps & {
    hydrateAsins: (payload: hydrateCatalogPayload) => void;
    updateCurrentPath: (payload: string) => void;
    setSelectedSongAsin: (payload: string) => void;
    removeSelectedSongAsin: () => void;
    getCsvFile: (payload: createCsvFileRequestPayload) => void;
    sendClientMetrics: (payload: clientMetricsPayload) => void;
};

type Props = DispatchProps & StateProps & RouteComponentProps<ViewProps>;

type State = RequiredReportingState & {
    loadedCount: number;
    trackCatalogItem?: track;
};

// Displays a list of playlists. Entry point is "See All" on a playlists table
class PlaylistsScreen extends React.Component<Props, State> {
    private startTime = Date.now();

    constructor(props: any) {
        super(props);
        this.state = {
            refreshing: false,
            showError: false,
            loadedCount: 0,
        };
        this.props.updateCurrentPath(window.location.pathname);
    }

    componentDidMount() {
        // If artist isnt selected, redirect to select artist page
        if (!this.props.selectedArtist) {
            this.props.history?.replace(paths.artistSelect);
            return;
        }

        this.setState({ finishedInitialLoad: false });
        this.startTime = Date.now();
        this.checkUrlParam();
        this.getReports();

        this.loadMore();
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.selectedRange !== prevProps.selectedRange) {
            this.getReports();
        } else if (
            (this.props.selectedRange === TimeRange.Custom &&
                this.props.startDate !== prevProps.startDate) ||
            this.props.endDate !== prevProps.endDate
        ) {
            this.getReports(true);
        } else if (this.props.selectedArtist !== prevProps.selectedArtist) {
            this.getReports();
        } else if (this.props.selectedSongAsin !== prevProps.selectedSongAsin) {
            this.getReports(true);
        }

        // Loaded everything for the first time
        if (
            this.state.finishedInitialLoad === false &&
            this.state.loadedReports &&
            this.props.catalogBuildCompleted &&
            this.props.hydrationInProgress &&
            !prevProps.hydrationInProgress
        ) {
            this.setState({ finishedInitialLoad: true });
        }

        if (this.props.error !== prevProps.error) {
            this.setState({ showError: this.props.error !== undefined });
        }

        if (prevProps.inProgress && !this.props.inProgress) {
            this.setState({ refreshing: false, loadedReports: true });
            this.loadMore();
        }
    }

    componentWillUnmount() {
        this.props.removeSelectedSongAsin();
    }

    render() {
        if (this.props.inProgress && !this.state.refreshing) {
            return <Loading />;
        }
        const track = this.state.trackCatalogItem;

        const csvBasePayload = {
            timeRange: this.props.selectedRange,
            artistName: this.props.artist?.title || "",
            startDate: this.props.startDate,
            endDate: this.props.endDate,
            teamId: this.props.teamId,
            locale: this.props.userLocale,
            requestPath: pagePath,
            csvExportType: "featuredInPlaylists" as CsvType,
            contentName: track?.title,
            playlistData: this.props.playlists,
        };

        // if statement below guarantees either profile head or common header props are defined
        let commonHeaderProps: CommonHeaderProps | undefined;
        let profileHeadProps: ProfileHeadProps | undefined;
        let csvPayload: createCsvFileRequestPayload;

        if (track) {
            csvPayload = {
                ...csvBasePayload,
                csvButtonId: CsvButtonId.SongSeeAllFeaturedPlaylists,
            };
            profileHeadProps = {
                title: track.title || "",
                imageDescription: track.title,
                imageSource:
                    track.images && track.images.artLarge
                        ? track.images.artLarge
                        : "",
                fallbackImage: ImageList.placeholder_track,
                subtitle: getLocalizedString(this.props.bundleMap, {
                    bundleId: bundleIds.REPORTS_STRINGS,
                    stringId: stringIds.Reports.songsLabel,
                }),
                id: testIDPrefix + testIDSuffixes.profileImage,
                showCsvButton:
                    !DeviceUtil.isMobileWrapper() &&
                    this.props.hasCsvExportCapabilities &&
                    !!this.props.playlists.length,
                csvDownload: () => {
                    this.props.sendClientMetrics(
                        buildUIClickPayload(
                            buttonIds.CsvExport.downloadPlaylists,
                            pageIds.playlists,
                            this.props.selectedArtist,
                            EntityType.ARTIST
                        )
                    );
                    this.props.getCsvFile(csvPayload);
                },
                csvTooltipMessage: getLocalizedString(this.props.bundleMap, {
                    bundleId: bundleIds.CSV_STRINGS,
                    stringId: stringIds.Csv.TooltipMessage.FeaturedInPlaylists,
                }),
                csvButtonLoading:
                    this.props.csvButtonLoadingStatus[
                        "SongSeeAll-FeaturedPlaylists"
                    ],
            };
        } else {
            csvPayload = {
                ...csvBasePayload,
                csvButtonId: CsvButtonId.ProgrammingSeeAllFeaturedPlaylists,
            };
            commonHeaderProps = {
                title: getLocalizedString(this.props.bundleMap, {
                    bundleId: bundleIds.REPORTS_STRINGS,
                    stringId: stringIds.Reports.playlistLabel,
                }),
                id: `${testIDPrefix}_Header`,
                showCsvButton:
                    !DeviceUtil.isMobileWrapper() &&
                    this.props.hasCsvExportCapabilities &&
                    !!this.props.playlists.length,
                csvDownload: () => {
                    this.props.sendClientMetrics(
                        buildUIClickPayload(
                            buttonIds.CsvExport.downloadPlaylists,
                            pageIds.playlists,
                            this.props.selectedArtist,
                            EntityType.ARTIST
                        )
                    );
                    this.props.getCsvFile(csvPayload);
                },
                csvTooltipMessage: getLocalizedString(this.props.bundleMap, {
                    bundleId: bundleIds.CSV_STRINGS,
                    stringId: stringIds.Csv.TooltipMessage.FeaturedInPlaylists,
                }),
                csvButtonLoading:
                    this.props.csvButtonLoadingStatus[
                        "ProgrammingSeeAll-FeaturedPlaylists"
                    ],
            };
        }

        const selectedAsin = this.props.match.params.trackAsin;
        let newPlaylists;
        if (selectedAsin) {
            newPlaylists = getNewPlaylistAsins(
                this.props.recentlyAddedToPlaylistData,
                selectedAsin
            );
        }

        const featuredPlaylistsHeader = [
            getLocalizedString(this.props.bundleMap, {
                bundleId: bundleIds.REPORTS_STRINGS,
                stringId: stringIds.Reports.playlistsLabel,
            }),
            "",
            getLocalizedString(this.props.bundleMap, {
                bundleId: bundleIds.REPORTS_STRINGS,
                stringId: stringIds.Reports.streamsLabel,
            }),
        ];
        const featuredPlaylists: TableRowProps[] = generatePlaylistRows(
            this.props.playlists.slice(0, this.state.loadedCount),
            this.props.catalog,
            this.props.hydratingAsins,
            this.props.failedAsins,
            this.props.history?.push,
            testIDPrefix + "_PlaylistTableRows",
            this.props.bundleMap,
            newPlaylists
        );

        const image = undefined;
        const bgImage = image || this.props.artist?.images?.artSmall;
        return (
            <Container
                fluid={true}
                className="rootContainer"
                style={{
                    ...rootStyles.containerStyles.rootViewContainer,
                    ...rootStyles.containerStyles.tableContainerStyle,
                }}
            >
                {/* {bgImage &&
                    <Image
                        blurRadius={rootStyles.blurAmount}
                        style={rootStyles.imageStyles.backgroundImage}
                        source={{ uri: bgImage }}
                    />
                } */}
                {track && profileHeadProps && (
                    <ProfileHead {...profileHeadProps} />
                )}
                {!track && commonHeaderProps && (
                    <CommonHeader {...commonHeaderProps} />
                )}
                <Row>
                    {this.state.showError && (
                        <InlineError
                            text={getLocalizedString(this.props.bundleMap, {
                                bundleId: bundleIds.ERRORS_STRINGS,
                                stringId: stringIds.Errors.reportsError,
                            })}
                            retryAction={this.getReports}
                            id={`${testIDPrefix}_Error`}
                        />
                    )}
                </Row>

                <Row>
                    <Col>
                        <Table
                            loading={this.state.refreshing}
                            loadMore={this.loadMore}
                            rows={featuredPlaylists}
                            labels={featuredPlaylistsHeader}
                            title={getLocalizedString(this.props.bundleMap, {
                                bundleId: bundleIds.REPORTS_STRINGS,
                                stringId:
                                    stringIds.Reports.featuredPlaylistsTitle,
                            })}
                            scrolling={true}
                            footer={
                                this.props.playlists.length <=
                                this.state.loadedCount ? (
                                    <div
                                        style={{
                                            height: rootStyles.spacers.huge,
                                        }}
                                    />
                                ) : (
                                    <IncrementalLoading />
                                )
                            }
                            id={testIDPrefix + "_PlaylistTable"}
                        />
                    </Col>
                </Row>
            </Container>
        );
    }

    private checkUrlParam = () => {
        const trackAsin = this.props.match.params.trackAsin;
        if (trackAsin) {
            this.props.setSelectedSongAsin(trackAsin);
            const trackCatalogItem = this.props.catalog.get(trackAsin) as track;

            if (trackCatalogItem) {
                this.setState({ trackCatalogItem: trackCatalogItem });
            }
        } else {
            this.props.removeSelectedSongAsin();
        }
    };

    private loadMore = () => {
        if (!this.props.signedIn) {
            return;
        }

        const nextCount: number = hydrationHelpers.hydrateAsinsBatch(
            this.state.loadedCount,
            _.map(this.props.playlists, (playlist) => {
                return {
                    asin: playlist.id,
                };
            }),
            this.props.hydrateAsins,
            CatalogItemType.Playlists,
            this.props.userLocale
        );

        this.setState({
            loadedCount: nextCount,
        });
    };

    private getReports = (isRefresh?: boolean) => {
        if (
            !this.props.signedIn ||
            !this.props.selectedArtist ||
            !this.props.teamId
        ) {
            return;
        }

        this.setState({
            loadedCount: HYDRATION_COUNT,
        });

        const timeRangePayload: timeRangePayload = {
            timeRange: this.props.selectedRange,
            startDate: this.props.startDate,
            endDate: this.props.endDate,
        };

        const trackAsin = this.props.match.params.trackAsin;

        const featuredPlaylistsQuery = {
            ...generateQueryWithoutGranularity(
                timeRangePayload,
                guessTimeZone(),
                300
            ),
            ...(trackAsin && { trackAsin: trackAsin }),
        };

        const query: queryRequest = {
            artistAsin: this.props.selectedArtist,
            featuredPlaylistsQueryV2: featuredPlaylistsQuery,
        };

        const payload: overviewRequestPayload = {
            query: query,
            requestId: this.props.reportingKey,
            requestPath: paths.playlists,
            isRefresh: isRefresh,
            teamId: this.props.teamId,
        };

        this.props.getReport(payload);
    };
}

function mapStateToProps(state: RootState) {
    return playlistScreenSelector(state, paths.playlists);
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return {
        getReport: (payload: overviewRequestPayload) =>
            dispatch(reportingActions.getReports(payload)),
        clearFailedAsins: () => dispatch(catalogActions.clearFailedAsins()),
        hydrateAsins: (payload: hydrateCatalogPayload) =>
            dispatch(catalogActions.hydrateAsins(payload)),
        updateCurrentPath: (payload: string) =>
            dispatch(userActions.updateCurrentPath(payload)),
        setSelectedSongAsin: (payload: string) =>
            dispatch(reportingActions.setSelectedSongAsin(payload)),
        removeSelectedSongAsin: (payload: string) =>
            dispatch(reportingActions.removeSelectedSongAsin()),
        getCsvFile: (payload: createCsvFileRequestPayload) =>
            dispatch(csvActions.createCsvFile(payload)),
        sendClientMetrics: (payload: clientMetricsPayload) =>
            dispatch(clientMetricsActions.sendClientMetrics(payload)),
    };
}

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(PlaylistsScreen)
);
